<template>
  <Layout>
    <div class="nftmax-personals">
      <h2 class="nftmax-personals__title">Frequently Asked Questions</h2>
      <el-collapse v-model="activeNames" accordion>
        <el-collapse-item
          title="What is Lavita AI platform and how does it work?"
          name="1"
        >
          <div class="collapse-item">
            Lavita AI provides an all-in-one platform that empowers users to:
            <br /><br />
            • Access their health data, including Electronic Health Records (EHR) in a unified portal
            <br /><br />
            • Utilize their health data to receive personalized and relevant health and wellness advice based on the latest Generative AI and Large Language Model (LLM) engines
            <br /><br />
            • Ensure privacy and security of their data based on end-to-end encryption technology and confidential-computing techniques.
            <br /><br />
            There are two ways you can use Lavita AI platform:
            <br /><br />
            • Use "My Data" section of the platform, to connect your health data such as Electronic Health Records, medical images and PDF files, and create a unified, secure repository of your health and wellness data
            <br /><br />
            • Use "Secure AI Companion" to ask any health and wellness related questions from Lavita AI's chatbot. To receive personalized answers, you can securely connect your health data to chatbot, while ensuring your data remain secure and private, even when it is being processed by AI models.
            <br /><br />
            Please read more about Lavita AI's data security and encryption technologies in the remainder of this FAQ.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title='What is "Confidential-Computing" technology and how Lavita AI is using it?'
          name="2"
        >
          <div class="collapse-item">
            Confidential computing is a cloud computing technology designed to protect data while it is being used (processed) not just when it's stored or traveling over a network. Traditional cloud services typically encrypt data while at rest (e.g. stored on hard drive) or in transit over the network. But once data is loaded into memory for processing, it is unencrypted and visible to the system (e.g. cloud operators or AI model providers).
            <br /><br />
            <strong>Confidential computing adds a critical protection layer: it keeps data encrypted even in memory by processing it in a protected, isolated environment called a Trusted Execution Environment (TEE). Your data is not accessible to cloud operators, hypervisors, AI model providers, or administrators.</strong>
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="Why using Lavita AI is fundamentally more secure than using ChatGPT, Claude, Perplexity, or other types of commercially available chatbots?"
          name="3"
        >
          <div class="collapse-item">
            When you type data into ChatGPT or Claude through their standard web interfaces, your data is sent over the network and processed on the AI model provider's servers. These providers (e.g. OpenAI or Anthropic) may retain prompts to improve their AI models or retain your data in order create a user profile. Even when your data is encrypted in storage, your data is decrypted in memory for the models to see it — and that memory is not protected from cloud or platform access, meaning that the cloud providers and AI model providers have access to your unencrypted data. As a result, your data is not confidential during processing in these commercially available chatbots.
            <br /><br />
            <strong>In contrast, Lavita AI's secure chatbot is built inside a confidential virtual machine (VM) and stays encrypted in main memory. The cloud operators, the AI model providers, or even Lavita company itself, can't read your data while it's being processed. This means your data remains confidential at all stages, including in use — enabling strong privacy for sensitive workloads like healthcare records.</strong>
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="Should I be worried that my health data is shared with LLM companies such as OpenAI and Anthropic, when I use Lavita AI?"
          name="4"
        >
          <div class="collapse-item">
            No. Lavita AI does not use APIs from commercial chatbot providers such as OpenAI, Anthropic, or Perplexity to process your data. Lavita AI has built fully secure and private chatbot tech stack from ground up based on confidential computing technology and open-source LLM models, to bring you maximum security and privacy when handling your health data.
            <br /><br />
            The commercial AI chatbots (ChatGPT, Claude, etc.) do not protect your data while it's actively being processed — your text is decrypted into memory and visible to the runtime environment. In contrast, Lavita AI's confidential computing platform isolates and encrypts your data even during computation, making it truly confidential. This is absolutely essential for handling your sensitive information such as personal health data.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="Are there any rewards for using Lavita AI platform?"
          name="5"
        >
          <div class="collapse-item">
            Yes, Lavita AI incentivizes individuals to connect their health data, and be rewarded through "vLAVITA" digital tokens, built on the Theta blockchain ecosystem. Lavita AI's confidential computing technology ensures that users' data remains private and secure during the computation phase. Users can use vLAVITA rewards to subscribe to the "pro" version of Lavita AI platform to access the advanced features of the platform.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="How do I link my EHR data such as MyChart account to Lavita AI platform?"
          name="6"
        >
          <div class="collapse-item">
            Through "My Data" section of the platform, users can authenticate their local device to access their health records from Epic's MyChart (other EHR platforms are currently being integrated and will be rolled out in future). The EHR data is immediately encrypted upon retrieval from MyChart, with private key only accessible to the users themselves. Thus, only users can view and access their health data by using their unique private key.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="What advantages does Lavita offer over traditional health data management systems?"
          name="7"
        >
          <div class="collapse-item">
            Lavita AI platform is an all-in-one data platform to connect your various health data into one interface and use advanced LLM-based AI models to receive personalized health and wellness recommendations, all the while ensuring your data privacy through encryption and confidential computing technologies.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="How can I manage permissions to access my health data on Lavita platform?"
          name="8"
        >
          <div class="collapse-item">
            After connecting their Lavita account with their EHR accounts, users need to authenticate access to their data through their private key (which only users have access to, i.e., Lavita AI never stores private keys), as well as 2FA through one-time-passcode sent to users registered email address.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="What does it mean to tokenize health data, and does Lavita AI uses this method?"
          name="9"
        >
          <div class="collapse-item">
            Lavita AI never stores personal data on the blockchain and never tokenizes health data for use on the blockchain. Users' data is always encrypted, during storage, transmission, and compute, and only users themselves have the private key to access and decrypt their data.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="What types of health insights can I expect to receive from Lavita's AI chatbot?"
          name="10"
        >
          <div class="collapse-item">
            Lavita AI chatbot is a Medical AI Engine powered by state-of-the-art Large Language Models (LLMs) that are continually fine-tuned and improved for health and medical applications, using data from published research as well as input from medical experts. Lavita AI aims to bring the most personalized health advice for users, while ensuring their data privacy.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="Are there any costs for using Lavita AI platform?"
          name="11"
        >
          <div class="collapse-item">
            Lavita AI platform provides "freemium" option for users, where they can access many features of the platform for free, and can enjoy some premium features for a low monthly subscription fee.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="How can I select topics that I am interested in My Knowledge Hub and My Podcasts?"
          name="12"
        >
          <div class="collapse-item">
            You can select topics that you are interested in on top of the screen by going into My Knowledge Hub tab. Once you select the topics you are interested, Lavita AI engine provide frequent (multiple times per week) updates for you to receive new podcasts and articles related to the topics you are interested in.
          </div>
        </el-collapse-item>
        <el-collapse-item
          title="How can I provide feedback or get technical support for issues?"
          name="13"
        >
          <div class="collapse-item">
            Users can utilize the "Feedback" section of the navigation menu to provide feedback about the platform and receive help with any technical questions.
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/components/Partials/Layout";

export default {
  components: {
    Layout,
  },
  data() {
    return {
      activeNames: "",
    };
  },
  methods: {},
};
</script>

<style scoped>
.collapse-item {
  padding-left: 20px;
}
.circle-list {
  list-style-type: disc;
}
</style>
